/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DropdownModel } from '../../../../../../../core/models';
import { FallbackPagesApiService, HealthChecksApiService, Layer7ApiService } from '../../../../http';
import { FormBuilder, Validators } from '@angular/forms';
import { L7BackendServiceModel } from '../../../../models';
import { LanguageService, LogService, ModalService, UtilsService } from '../../../../../../../core/services';
import { LbErrorService, WizardService, WizardTemplateService } from '../../../../services';
import { ServiceBaseComponent } from '../../serviceBase.component';
import { WizardSubStepTitlesEnum, WizardTitleEnum } from '../../../../enums';
import { forkJoin } from 'rxjs';
import { haproxyTimeValidation } from '../../../../directives';
import {
    integerValidation,
    ipValidator,
    nameValidation,
    portRangeValidation
} from '../../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-l7backend',
    templateUrl: './layer7-backend.component.html',
    styleUrls: ['./layer7-backend.component.scss'],
})

export class Layer7BackendComponent extends ServiceBaseComponent implements OnInit {
    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        balanceModes: [],
        checkScripts: [],
        fallbackConfig: [],
        healthCheckTypes: [],
        httpPipelineModes: [],
        modes: [],
        persistenceModes: [],
        proxyProtocols: []
    };

    /**
     * A boolean to determine if the form should be full width or not.
     */
    public isFormFullWidth: boolean = false;

    /**
     * List of backends
     * @private
     */
    private backends: L7BackendServiceModel[] = [];

    /**
     * PersistenceMode
     */
    private persistenceMode: string = this.serviceForm &&
        this.serviceForm.get(WizardSubStepTitlesEnum.PERSISTENCE + '.persistenceMode').value;

    /**
     * HealthCheckType
     */
    private healthCheckType: string = this.serviceForm &&
        this.serviceForm.get(WizardSubStepTitlesEnum.HEALTH + '.healthCheck').value;

    constructor(
        private fallbackPagesApiService: FallbackPagesApiService,
        private healthCheckScriptsApiService: HealthChecksApiService,
        private layer7ApiService: Layer7ApiService,
        cdr: ChangeDetectorRef,
        formBuilder: FormBuilder,
        languageService: LanguageService,
        lbErrorService: LbErrorService,
        logService: LogService,
        modalService: ModalService,
        route: ActivatedRoute,
        utilsService: UtilsService,
        wizardService: WizardService,
        wizardTemplateService: WizardTemplateService
    ) {
        super(
            cdr,
            formBuilder,
            languageService,
            lbErrorService,
            logService,
            modalService,
            route,
            utilsService,
            wizardService,
            wizardTemplateService
        );

        this.setupBaseSubscriber();
        this.componentTitle = WizardTitleEnum.BACKENDS;
    }

    ngOnInit() {
        this.setupFormGroup();
        this.setupFormSubscribers();
        this.setupWizardSubscribers();
        this.initLoading();
    }

    /**
     * Set Rip ids in the form.
     * @param event
     */
    public setRipIds(event: any): void {
        this.serviceForm.get(WizardSubStepTitlesEnum.RIP_IDS + '.ripIds').setValue(event);
    }

    /**
     * If authentication is turned on make the username and password fields active.
     * @param required
     * @private
     */
    private toggleAuthentication(required: boolean): void {
        const username = this.serviceForm.get(WizardSubStepTitlesEnum.HEALTH + '.healthCheckUsername');
        const password = this.serviceForm.get(WizardSubStepTitlesEnum.HEALTH + '.healthCheckPassword');

        if (required) {
            username.enable();
            username.setValidators(Validators.required);
            password.enable();
            password.setValidators(Validators.required);
        } else {
            username.disable();
            username.setValidators(null);
            password.disable();
            password.setValidators(null);
        }

        username.updateValueAndValidity({ emitEvent: false });
        password.updateValueAndValidity({ emitEvent: false });
    }

    /**
     * Set up the form for the view.
     * @private
     */
    private setupFormGroup(): void {
        // Define our form builder.
        this.serviceForm = this.formBuilder.group({
            [WizardSubStepTitlesEnum.GENERAL]: this.formBuilder.group({
                id: [''], // <- Used outside this component.
                // General content
                name: ['', [nameValidation, Validators.required]],
                mode: [{ value: 'tcp', disabled: true }, Validators.required],
                balanceMode: ['leastconn', Validators.required],
                encryptToBackend: [false],
                enableGzipCompression: [false],
            }),
            [WizardSubStepTitlesEnum.HEADERS]: this.formBuilder.group({
                // Headers content
                headerIds: [[], null],
            }),
            [WizardSubStepTitlesEnum.PERSISTENCE]: this.formBuilder.group({
                // Persistence content
                persistenceMode: ['sourceIP', Validators.required],
                httpCookieName: ['', [nameValidation, Validators.required]],
                applicationCookieName: ['', [nameValidation, Validators.required]],
                XFFIPPosition: [-1], // this doesn't validate minus numbers -> isIntegerValidation
                persistenceTimeout: ['15m', haproxyTimeValidation],
                httpCookieMaxIdle: ['30m', haproxyTimeValidation],
                httpCookieMaxLife: ['12h', haproxyTimeValidation],
                persistenceTableSize: [10240, integerValidation],
                persistenceClearStickOnDrain: [false],
            }),
            [WizardSubStepTitlesEnum.HEALTH]: this.formBuilder.group({
                // Health check content
                healthCheck: ['connectToPort'],
                healthCheckPort: [null, portRangeValidation],
                healthCheckRequestToSend: ['/'],
                healthCheckResponseExpected: [''],
                authentication: [false],
                // validation for these two are toggled depending on above input
                healthCheckUsername: [null],
                healthCheckPassword: [null],
                healthCheckScriptId: [null],
                healthCheckHostHeader: [''],
                healthCheckInterval: ['4s', haproxyTimeValidation],
                healthCheckRise: [2, integerValidation],
                healthCheckFall: [2, integerValidation],
            }),
            [WizardSubStepTitlesEnum.CONNECTION_OPTIONS]: this.formBuilder.group({
                // Connection options
                httpPipeline: ['httpKeepAlive'],
                httpPretendKeepalive: [false],
                httpAcceptInvalidResponse: [false],
                httpIdleConnectionReuse: [false],
                httpRequestTimeout: [true],
                tunnelTimeout: ['1h'],
                TCPKeepalive: [false],
                connectionRedispatch: [true],
                abortOnClose: [false],
                slowStartTime: ['3s', haproxyTimeValidation],
                backendSourceAddress: ['', ipValidator],
                enableServerConnTimeout: [true],
                serverTimeout: ['43s', haproxyTimeValidation],
            }),
            [WizardSubStepTitlesEnum.LAYER_7_ADVANCED]: this.formBuilder.group({
                // Advanced options
                enableFeedbackAgent: [false],
                feedbackAgentPort: [3333, portRangeValidation],
                feedbackAgentInterval: ['2s', haproxyTimeValidation],
                fallbackServerId: ['none'],
                fallbackPersistence: [false],
                enableTProxy: [false],
                sendProxyProtocol: ['off'],
            }),
            [WizardSubStepTitlesEnum.RIP_IDS]: this.formBuilder.group({
                ripIds: [[], Validators.required], // <- Used in real server component.
            })

            // TODO in docs but missing from here should there be a field for these?
            // httpReuseConnection
            // persistenceTableIPVersion
        });

        this.setupClassVariablesFromForm(WizardTitleEnum.BACKENDS);
    }

    /**
     * Setup form subscribers for required inputs and inputs that change the steps in the wizard.
     * @private
     */
    private setupFormSubscribers(): void {
        // Set up our form value changes subscriber
        this.serviceForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    // Validate the form each step of the way. Use debounce to reduce the number of times the callback is called.
                    this.utilsService.debounce('backends', () => {
                        if (this.isCurrentStepValid()) {
                            this.outputFormValues();
                        }
                    }, 200);
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.name').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: name => {
                    this.checkNameForDuplicates(name, this.backends);
                }
            });

        // Set up value change listeners for inputs that make changes to another elements.
        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.mode').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: mode => {
                    this.toggleHTTPMode(mode);
                    this.setUpDropdownContent();
                    this.resetModeBasedDropdowns();

                    // Option requires sub steps to be updated
                    this.wizardService.setSubStepVisibleState(WizardSubStepTitlesEnum.HEADERS, this.httpMode);
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.PERSISTENCE + '.persistenceMode').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: persistenceMode => {
                    this.persistenceMode = persistenceMode;
                    this.togglePersistenceMode();
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.HEALTH + '.healthCheck').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: healthCheck => {
                    this.healthCheckType = healthCheck;
                    if (this.wizardService.wizardMode !== 'partner') {
                        this.toggleHealthChecks();
                        this.toggleExternalHealthScriptValidation(healthCheck);
                    }
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.HEALTH + '.authentication').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: authentication => {
                    this.toggleAuthentication(authentication);
                }
            });
    }

    /**
     * Setup wizard subscribers
     * @private
     */
    private setupWizardSubscribers(): void {
        /**
         * Advanced Mode Subscriber
         * Note: We subscribe to whether the advanced mode button has been toggled.
         */
        this.wizardService.wizardModeEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                advancedState => {
                    this.advancedMode = advancedState;

                    if (this.advancedMode !== 'partner') {
                        this.toggleAdvanceModeSteps();
                        this.toggleAdvanceModeInputs();
                    }
                }
            );

        this.wizardService.subStepNameEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(step => {
                const fullWithPages: string[] = [
                    WizardSubStepTitlesEnum.HEADERS, WizardSubStepTitlesEnum.RIP_IDS
                ];

                this.isFormFullWidth = fullWithPages.includes(step);
            });
    }

    /**
     * Sets advance mode steps.
     * @private
     */
    private toggleAdvanceModeSteps(): void {
        const advancedMode: boolean = this.advancedMode === 'advanced';
        this.wizardService.setSubStepVisibleState(WizardSubStepTitlesEnum.LAYER_7_ADVANCED, advancedMode);
        this.wizardService.setSubStepVisibleState(WizardSubStepTitlesEnum.CONNECTION_OPTIONS, advancedMode);
    }

    /**
     * Toggle the inputs that are only part of advanced mode.
     * @private
     */
    private toggleAdvanceModeInputs(): void {
        const advancedMode = this.advancedMode === 'advanced';

        this.inputStateStore['encryptToBackend'] = { hidden: !advancedMode };
        this.inputStateStore['enableGzipCompression'] = { hidden: !advancedMode };

        this.togglePersistenceMode();
        this.toggleHealthChecks();
    }

    /**
     * Loads both the Health checks and fallback pages and set the values.
     * @private
     */
    private initLoading(): void {
        const apiCalls = {
            checkScripts: this.healthCheckScriptsApiService.getHealthChecksByService('layer-7'),
            fallbackConfig: this.fallbackPagesApiService.getAll(),
            backends: this.layer7ApiService.getBackends()
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                (dataGroup: object): void => {
                    const serviceError: any = this.getErrorForService(WizardTitleEnum.BACKENDS);
                    const template: any = this.getTemplate(WizardTitleEnum.BACKENDS, serviceError);

                    // Don't allow 'add another' if editing.
                    if (this.utilsService.isServiceId(template.id?.value)) {
                        this.wizardService.excludeFromAddAnother = WizardTitleEnum.BACKENDS;
                    }

                    this.setUpDropdownApiData(dataGroup);
                    const services: L7BackendServiceModel[] = this.formatServices(dataGroup['backends'], WizardTitleEnum.BACKENDS);
                    this.backends = this.filterServicesByName(template, services);

                    this.patchTemplateToForm(template);

                    if (this.templateIndex > 0) {
                        this.addACLForAdditionalBackends();
                    }

                    // Once everything is loaded check to see if there are any error in the controller state
                    if (serviceError?.error?.length) {
                        this.loadedWithErrors(serviceError.error);
                    }
                }
            );
    }

    /**
     * Set up dropdowns with api data.
     * @param dataGroup
     * @private
     */
    private setUpDropdownApiData(dataGroup: object): void {
        if (dataGroup['checkScripts']?.length) {
            this.dropdown.checkScripts = this.getHealthCheckValues(dataGroup['checkScripts']);
        }

        if (dataGroup['fallbackConfig']?.length) {
            this.dropdown.fallbackConfig = this.getFallbackServers(dataGroup['fallbackConfig']);
        }
    }

    /**
     * Get the name and ids from the external health check scripts to populate dropdown.
     * @param fallbackConfig
     * @private
     */
    private getHealthCheckValues(fallbackConfig: any): DropdownModel[] {
        return this.utilsService.convertArrayToDropDown(fallbackConfig, 'id', 'name');
    }

    /**
     * Get the fallback server's ip addresses.
     * @param fallbackConfig
     */
    private getFallbackServers(fallbackConfig: any): DropdownModel[] {
        const fallbackData: DropdownModel[] = this.utilsService.convertArrayToDropDown(fallbackConfig, 'id', 'name');
        fallbackData.unshift({ key: 'none', value: 'none' });

        return fallbackData;
    }

    /**
     * Add ACL for additional backends.
     * @private
     */
    private addACLForAdditionalBackends(): void {
        const acl = this.wizardService.controllerState[WizardTitleEnum.ACLS];
        const clonedAcl = this.utilsService.duplicateObject(acl);
        const section = Array.isArray(clonedAcl) ? clonedAcl[0] : clonedAcl;
        section.id.value = 'temp-' + Date.now() + Math.random();
        section.redirectLocation.value = '$' + (this.templateIndex + 1);
        const step = { service: WizardTitleEnum.ACLS, index: 0 };
        this.wizardService.addService(step, false, section);
    }

    /**
     * Initialises the form build.
     * @param template
     * @private
     */
    private patchTemplateToForm(template: any): void {
        // Setup dropdowns before patching values
        this.setUpDropdownContent();

        // Populate our patch
        const templatePatch = this.createTemplatePatch(template);

        // Format name and prefix
        const newLabel = this.wizardService.wizardMode === 'partner'
            ? this.getFrontendName()
            : template.name.value;

        this.formatLabel(templatePatch, newLabel);

        // Add the frontend mode to the template patch before patching.
        const frontendMode = this.getFrontendMode();
        if (frontendMode) {
            templatePatch['mode'] = frontendMode;

            if (frontendMode === 'http') {
                // If there is only one option in the mode dropdown it will be disabled and therefore not emit a value
                this.wizardService.controllerState[WizardTitleEnum.BACKENDS][this.templateIndex].mode.value = 'http';
            }
        }

        // Check if we are in advanced mode and set the correct states on the inputs.
        this.advancedMode = this.wizardService.wizardMode;
        if (this.advancedMode === 'advanced') {
            this.toggleAdvanceModeSteps();
            this.toggleAdvanceModeInputs();
        }

        // Patch our hidden state
        this.patchTemplateHiddenState(template, this.inputStateStore);
        this.registerSubSteps(WizardTitleEnum.BACKENDS);

        // Apply patch to form
        this.applyPatchAndValidate(templatePatch);

        // This input is an advanced feature but is required by some templates so needs to enabled here.
        if (template['authentication']?.value && !template['authentication']?.hidden) {
            this.inputStateStore['authentication'].hidden = false;
            this.serviceForm.get(WizardSubStepTitlesEnum.HEALTH + '.authentication').enable();
        }

        // Ensure the step is loaded in the correct width (mainly when going backwards in the wizard).
        const fullWidthPages: string[] = [WizardSubStepTitlesEnum.HEADERS, WizardSubStepTitlesEnum.RIP_IDS];
        this.isFormFullWidth = fullWidthPages.includes(this.currentSubStepName);

        // Patching can change the view.
        this.cdr.detectChanges();

        // Tell the wizard to show the content
        this.wizardService.isLoading = false;

        // Once patched output the values to the controller state.
        this.outputFormValues();
    }

    /**
     * Gets the first frontend mode from the frontendModes @Input.
     * @private
     */
    private getFrontendMode(): string {
        return this.utilsService.getObjectValue(
            this.wizardService.controllerState, `${WizardTitleEnum.FRONTENDS}.${this.templateIndex}.mode.value`
        );
    }

    /**
     * Toggle HTTP Mode
     * @param mode
     * @private
     */
    private toggleHTTPMode(mode: string): void {
        this.httpMode = mode === 'http';
        this.inputStateStore['headers'] = { hidden: !this.httpMode };
    }

    /**
     * Toggles the hidden state of persistence mode options when persistence mode is changed.
     * @private
     */
    private togglePersistenceMode(): void {
        const updateFormInputState = this.updateFormInputState;
        const formController = this.serviceForm.get(WizardSubStepTitlesEnum.PERSISTENCE);
        const hideAll = [
            'httpCookieName',
            'applicationCookieName',
            'XFFIPPosition',
            'httpCookieMaxIdle',
            'httpCookieMaxLife',
            'persistenceTimeout',
            'persistenceTableSize',
            'persistenceClearStickOnDrain'
        ];

        updateFormInputState(formController, this.inputStateStore, hideAll, true);
        switch (this.persistenceMode) {
            case 'httpCookie':
                updateFormInputState(formController, this.inputStateStore, ['httpCookieName']);
                if (this.advancedMode === 'advanced') {
                    updateFormInputState(formController, this.inputStateStore, ['httpCookieMaxIdle', 'httpCookieMaxLife']);
                }
                break;
            case 'httpCookieAndSourceIP':
                updateFormInputState(formController, this.inputStateStore, ['httpCookieName']);
                if (this.advancedMode === 'advanced') {
                    updateFormInputState(
                        formController,
                        this.inputStateStore,
                        // eslint-disable-next-line max-len
                        ['httpCookieMaxIdle', 'httpCookieMaxLife', 'persistenceTimeout', 'persistenceTableSize', 'persistenceClearStickOnDrain']
                    );
                }
                break;
            case 'RDPCookie':
            case 'sourceIP':
                if (this.advancedMode === 'advanced') {
                    updateFormInputState(
                        formController,
                        this.inputStateStore,
                        ['persistenceTimeout', 'persistenceTableSize', 'persistenceClearStickOnDrain']
                    );
                }
                break;
            case 'appSession':
                updateFormInputState(formController, this.inputStateStore, ['applicationCookieName']);
                if (this.advancedMode === 'advanced') {
                    updateFormInputState(
                        formController,
                        this.inputStateStore,
                        ['persistenceTimeout', 'persistenceTableSize', 'persistenceClearStickOnDrain']
                    );
                }
                break;
            case 'XFF':
                updateFormInputState(formController, this.inputStateStore, ['XFFIPPosition']);
                if (this.advancedMode === 'advanced') {
                    updateFormInputState(
                        formController,
                        this.inputStateStore,
                        ['persistenceTimeout', 'persistenceTableSize', 'persistenceClearStickOnDrain']
                    );
                }
                break;
            case 'sslSessID':
            case 'RDPSession':
                if (this.advancedMode === 'advanced') {
                    updateFormInputState(
                        formController,
                        this.inputStateStore,
                        ['persistenceTimeout', 'persistenceTableSize']
                    );
                }
                break;
        }
    }

    /**
     * Toggles the hidden state of health checks options when health checks is changed.
     * @private
     */
    private toggleHealthChecks(): void {
        const formStateSetHidden = this.updateFormInputState;
        const formController = this.serviceForm.get(WizardSubStepTitlesEnum.HEALTH);

        const hideAll = [
            'healthCheckPort',
            'healthCheckRequestToSend',
            'healthCheckResponseExpected',
            'healthCheckScriptId',
            'healthCheckHostHeader',
            'healthCheckUsername',
            'healthCheckInterval',
            'healthCheckRise',
            'healthCheckFall',
            'authentication'
        ];
        const negotiateGroup = [
            'healthCheckPort', 'authentication', 'healthCheckHostHeader'
        ];
        const timingGroup = [
            'healthCheckInterval', 'healthCheckRise', 'healthCheckFall'
        ];

        if (this.advancedMode === 'partner') {
            const hiddenFields = this.partnerHiddenItems(hideAll);
            formStateSetHidden(formController, this.inputStateStore, hiddenFields, true);
        } else {
            formStateSetHidden(formController, this.inputStateStore, hideAll, true);
        }

        switch (this.healthCheckType) {
            case 'negotiateHTTPGET':
            case 'negotiateHTTPSGET':
                formStateSetHidden(formController, this.inputStateStore, ['healthCheckRequestToSend', 'healthCheckResponseExpected']);
                if (this.advancedMode === 'advanced') {
                    formStateSetHidden(formController, this.inputStateStore, negotiateGroup.concat(timingGroup));
                }
                break;
            case 'negotiateHTTPHEAD':
            case 'negotiateHTTPOPTIONS':
            case 'negotiateHTTPSHEAD':
            case 'negotiateHTTPSOPTIONS':
                formStateSetHidden(formController, this.inputStateStore, ['healthCheckRequestToSend']);
                if (this.advancedMode === 'advanced') {
                    formStateSetHidden(formController, this.inputStateStore, negotiateGroup.concat(timingGroup));
                }
                break;
            case 'connectToPort':
                if (this.advancedMode === 'advanced') {
                    formStateSetHidden(formController, this.inputStateStore, timingGroup.concat('healthCheckPort'));
                }
                break;
            case 'externalScript':
                formStateSetHidden(formController, this.inputStateStore, ['healthCheckScriptId']);
                if (this.advancedMode === 'advanced') {
                    formStateSetHidden(formController, this.inputStateStore, timingGroup);
                }
                break;
            case 'mysql':
                if (this.advancedMode === 'advanced') {
                    formStateSetHidden(
                        formController, this.inputStateStore, timingGroup.concat(['healthCheckUsername', 'healthCheckPort'])
                    );
                }
                break;
        }
    }

    /**
     * Get partner hidden items.
     * @param hideAll
     * @private
     */
    private partnerHiddenItems(hideAll: string[]): string[] {
        const hiddenItems = [];
        for (const input in this.inputStateStore) {
            if (this.inputStateStore.hasOwnProperty(input)) {
                if (this.inputStateStore[input]?.hidden && hideAll.includes(input)) {
                    hiddenItems.push(input);
                }
            }
        }

        return hiddenItems;
    }

    /**
     * Toggle external health check script validation
     * @param healthCheck
     * @private
     */
    private toggleExternalHealthScriptValidation(healthCheck: string) {
        const externalScript = this.serviceForm.get(WizardSubStepTitlesEnum.HEALTH + '.healthCheckScriptId');
        if (healthCheck === 'externalScript' && this.dropdown.checkScripts.length) {
            externalScript.setValidators(Validators.required);
        } else {
            externalScript.setValidators(null);
        }
        this.cdr.detectChanges();
    }

    /**
     * Return dropdown structure array for mode
     * @private
     */
    private setUpDropdownContent(): void {
        this.setUpFrontendMode();

        this.dropdown.balanceModes = [
            <DropdownModel>{
                key: 'roundrobin',
                value: 'Weighted Round Robin'
            },
            <DropdownModel>{
                key: 'leastconn',
                value: 'Weighted Least Connected'
            },
            <DropdownModel>{
                key: 'first',
                value: 'First'
            },
            <DropdownModel>{
                key: 'source',
                value: 'Source'
            }
        ];

        if (!this.httpMode) {
            this.dropdown.balanceModes.push(
                <DropdownModel>{
                    key: 'rdp-cookie',
                    value: 'RDP Cookie'
                }
            );
        }

        this.dropdown.persistenceModes = this.setupPersistenceDropdown();

        // The dropdown values only used in when we are in http.
        const httpHealthCheckTypes = [
            <DropdownModel>{
                key: 'negotiateHTTPGET',
                value: 'Negotiate HTTP (GET)'
            },
            <DropdownModel>{
                key: 'negotiateHTTPHEAD',
                value: 'Negotiate HTTP (HEAD)'
            },
            <DropdownModel>{
                key: 'negotiateHTTPOPTIONS',
                value: 'Negotiate HTTP (OPTIONS)'
            },
            <DropdownModel>{
                key: 'negotiateHTTPSGET',
                value: 'Negotiate HTTPS (GET)'
            },
            <DropdownModel>{
                key: 'negotiateHTTPSHEAD',
                value: 'Negotiate HTTPS (HEAD)'
            },
            <DropdownModel>{
                key: 'negotiateHTTPSOPTIONS',
                value: 'Negotiate HTTPS (OPTIONS)'
            }
        ];

        this.dropdown.healthCheckTypes = [
            <DropdownModel>{
                key: 'connectToPort',
                value: 'Connect to port'
            }, <DropdownModel>{
                key: 'externalScript',
                value: 'External Script'
            },
            <DropdownModel>{
                key: 'mysql',
                value: 'MySQL'
            },
            <DropdownModel>{
                key: 'noCheck',
                value: 'No checks, always on'
            }];

        this.dropdown.healthCheckTypes = httpHealthCheckTypes.concat(this.dropdown.healthCheckTypes);

        this.dropdown.httpPipelineModes = [
            <DropdownModel>{
                key: 'httpClose',
                value: 'Http Close'
            },
            <DropdownModel>{
                key: 'httpServerClose',
                value: 'HTTP Server Close'
            },
            <DropdownModel>{
                key: 'httpKeepAlive',
                value: 'HTTP KeepAlive'
            },
        ];

        this.dropdown.proxyProtocols = [
            <DropdownModel>{
                key: 'v1',
                value: 'Send Proxy v1'
            },
            <DropdownModel>{
                key: 'v2',
                value: 'Send Proxy v2'
            },
            <DropdownModel>{
                key: 'v2SSL',
                value: 'Send Proxy v2SSL'
            },
            <DropdownModel>{
                key: 'v2SSLCN',
                value: 'Send Proxy v2SSLCN'
            },
            <DropdownModel>{
                key: 'off',
                value: 'Disabled'
            },
        ];
    }

    /**
     * Set the values of the dropdowns back to the page default, triggered when mode changes.
     * @private
     */
    private resetModeBasedDropdowns(): void {
        const balanceMode = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.balanceMode');
        const errorMessage = 'l7_mode_reset_error';

        if (this.httpMode && balanceMode.value === 'rdp-cookie') {
            balanceMode.setValue(null);
            balanceMode.setErrors({ 'modeChanged': errorMessage });
        }

        const persistenceMode = this.serviceForm.get(WizardSubStepTitlesEnum.PERSISTENCE + '.persistenceMode');
        const httpPersistence = ['appSession', 'httpCookie', 'httpCookieAndSourceIP', 'XFF'];
        if (!this.httpMode && httpPersistence.includes(persistenceMode.value)) {
            persistenceMode.setValue(null);
            persistenceMode.setErrors({ 'modeChanged': errorMessage });
        }

        const tcpPersistence = ['RDPSession', 'RDPCookie'];
        if (this.httpMode && tcpPersistence.includes(persistenceMode.value)) {
            persistenceMode.setValue(null);
            persistenceMode.setErrors({ 'modeChanged': errorMessage });
        }
    }

    /**
     * Helper function to set mode dropdown.
     * @private
     */
    private setUpFrontendMode(): void {
        // If the frontend mode is http then the backend can only be HTTP.
        const dropDownList = [
            <DropdownModel>{
                key: 'http',
                value: 'HTTP mode'
            }
        ];
        // If the frontend mode is http then the backend can only be http.
        if (this.getFrontendMode() !== 'http') {
            this.serviceForm.get(this.subStepEnum.GENERAL + '.mode').enable({ emitEvent: false });
            dropDownList.push(
                <DropdownModel>{
                    key: 'tcp',
                    value: 'TCP mode'
                },
            );
        }
        this.dropdown.modes = dropDownList;
    }

    /**
     * Set up persistence dropdown.
     * @private
     */
    private setupPersistenceDropdown(): any[] {
        const persistenceModes = [
            <DropdownModel>{
                key: 'none',
                value: 'None'
            },
            <DropdownModel>{
                key: 'sourceIP',
                value: 'Source IP'
            }
        ];

        if (this.httpMode) {
            persistenceModes.push(
                <DropdownModel>{
                    key: 'appSession',
                    value: 'App Session'
                },
                <DropdownModel>{
                    key: 'httpCookie',
                    value: 'HTTP Cookie'
                },
                <DropdownModel>{
                    key: 'httpCookieAndSourceIP',
                    value: 'HTTP Cookie and Source IP'
                },
                <DropdownModel>{
                    key: 'XFF',
                    value: 'X-Forwarded-For'
                },
            );
        }

        if (!this.httpMode) {
            persistenceModes.push(
                <DropdownModel>{
                    key: 'RDPSession',
                    value: 'RDP Session'
                },
                <DropdownModel>{
                    key: 'RDPCookie',
                    value: 'RDP Cookie'
                },
                <DropdownModel>{
                    key: 'sslSessID',
                    value: 'SSL Session ID'
                }
            );
        }

        return persistenceModes;
    }

    /**
     * Output frontend form values as L7 VirtualServiceModel.
     * @private
     */
    private outputFormValues(): void {
        if (!this.wizardService.isLoading) {
            const backendOj = this.flattenServiceFormValues(<L7BackendServiceModel>{});
            const propertiesToDelete = [
                'httpCookieMaxIdle',
                'httpIdleConnectionReuse'
            ];

            // Unset the fallbackServerId if set to 'none'. Avoids api complaining it cannot find 'none'.
            if (backendOj.fallbackServerId === 'none') {
                backendOj.fallbackServerId = '';
            }

            if (backendOj.healthCheckPort === null) {
                this.setPropertiesToIgnoreNullOrUndefined('healthCheckPort');
            }

            this.removeUnneededProperties(backendOj, propertiesToDelete);
            // loop through form values
            const template = this.wizardService.controllerState[WizardTitleEnum.BACKENDS][this.templateIndex];
            for (const property in backendOj) {
                if (backendOj.hasOwnProperty(property)) {
                    const visibility = this.inputStateStore[property]?.hidden;
                    template[property] = {
                        value: backendOj[property],
                        hidden: visibility !== undefined ? visibility : true
                    };
                }
            }
        }
    }
}
